import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform } from 'react-native';

import { useOnboardingFinished } from '@hooks/useOnboardingActive';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

export enum MemberTaskNudgeStep {
  ExploreTask = 'explore_task',
  FirstUpdate = 'first_update',
  SendMessage = 'send_message',
  SendFirstChat = 'send_first_chat',
  AddFileToChat = 'add_file_to_chat',
}

export const MEMBER_TASK_NUDGE_STEPS_ORDERED: MemberTaskNudgeStep[] = [
  MemberTaskNudgeStep.ExploreTask,
  MemberTaskNudgeStep.FirstUpdate,
  MemberTaskNudgeStep.SendMessage,
  MemberTaskNudgeStep.SendFirstChat,
  MemberTaskNudgeStep.AddFileToChat,
];

const STORAGE_KEY_TASK_ID = 'memberTaskNudgeTaskId';
const STORAGE_KEY_COMPLETED = 'memberTaskNudgeCompletedSteps';
const STORAGE_KEY_DISMISSED = 'memberTaskNudgeDismissed';
const STORAGE_KEY_STEP_DISMISS_COUNT = 'memberTaskNudgeStepDismissCount';
const STORAGE_KEY_STEP_LAST_DISMISS_AT = 'memberTaskNudgeStepLastDismissAt';
const STORAGE_KEY_CHAT_ID = 'memberTaskNudgeChatId';
const STORAGE_KEY_ADD_FILE_AVAILABLE_AFTER =
  'memberTaskNudgeAddFileAvailableAfter';
const STORAGE_KEY_FIRST_UPDATE_AVAILABLE_AFTER =
  'memberTaskNudgeFirstUpdateAvailableAfter';
const STORAGE_KEY_OTHER_MEMBER_SENT = 'memberTaskNudgeOtherMemberSent';

const RETRY_DELAYS_MS = [24 * 60 * 60 * 1000, 48 * 60 * 60 * 1000];
const MAX_STEP_DISMISSES = 2;

const isNative = Platform.OS !== 'web';

type MemberTaskNudgeContextType = {
  taskId: string | null;
  chatId: string | null;
  completedSteps: MemberTaskNudgeStep[];
  isDismissed: boolean;
  currentStep: MemberTaskNudgeStep | null;
  isComplete: boolean;
  isCurrentStepVisible: boolean;
  activateNudge: (newTaskId: string) => Promise<void>;
  deactivateNudge: () => Promise<void>;
  setChatId: (newChatId: string) => Promise<void>;
  otherMemberSentInNudgeChat: boolean;
  markOtherMemberSentInNudgeChat: () => Promise<void>;
  completeStep: (step: MemberTaskNudgeStep) => Promise<void>;
  dismissCurrentStep: () => Promise<void>;
};

const MemberTaskNudgeContext = createContext<
  MemberTaskNudgeContextType | undefined
>(undefined);

export const MemberTaskNudgeProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { trackEvent } = useMixpanel();
  // TA-10920 #19: Smart Nudges only surface once onboarding has finished.
  const onboardingFinished = useOnboardingFinished();
  const [taskId, setTaskId] = useState<string | null>(null);
  const [chatId, setChatIdState] = useState<string | null>(null);
  const [completedSteps, setCompletedSteps] = useState<MemberTaskNudgeStep[]>(
    []
  );
  const [isDismissed, setIsDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Per-step retry state (resets when step completes)
  const [stepDismissCount, setStepDismissCount] = useState(0);
  const [stepLastDismissAt, setStepLastDismissAt] = useState<number | null>(
    null
  );
  const [stepNow, setStepNow] = useState(() => Date.now());
  // Earliest timestamp AddFileToChat may appear (set 24h after SendFirstChat completes)
  const [addFileAvailableAfter, setAddFileAvailableAfter] = useState<
    number | null
  >(null);
  // Earliest timestamp FirstUpdate may appear (set 3h after ExploreTask completes)
  const [firstUpdateAvailableAfter, setFirstUpdateAvailableAfter] = useState<
    number | null
  >(null);
  // Sticky: true once any non-current-user message detected in the nudge chat
  const [otherMemberSentInNudgeChat, setOtherMemberSentInNudgeChat] =
    useState(false);

  useEffect(() => {
    if (!isNative) {
      setLoaded(true);
      return;
    }
    const loadState = async () => {
      const [
        storedTaskId,
        storedChatId,
        storedCompleted,
        storedDismissed,
        storedStepCount,
        storedStepLastAt,
        storedAddFileAvailableAfter,
        storedFirstUpdateAvailableAfter,
        storedOtherMemberSent,
      ] = await Promise.all([
        AsyncStorage.getItem(STORAGE_KEY_TASK_ID),
        AsyncStorage.getItem(STORAGE_KEY_CHAT_ID),
        AsyncStorage.getItem(STORAGE_KEY_COMPLETED),
        AsyncStorage.getItem(STORAGE_KEY_DISMISSED),
        AsyncStorage.getItem(STORAGE_KEY_STEP_DISMISS_COUNT),
        AsyncStorage.getItem(STORAGE_KEY_STEP_LAST_DISMISS_AT),
        AsyncStorage.getItem(STORAGE_KEY_ADD_FILE_AVAILABLE_AFTER),
        AsyncStorage.getItem(STORAGE_KEY_FIRST_UPDATE_AVAILABLE_AFTER),
        AsyncStorage.getItem(STORAGE_KEY_OTHER_MEMBER_SENT),
      ]);
      if (storedTaskId) setTaskId(storedTaskId);
      if (storedChatId) setChatIdState(storedChatId);
      if (storedCompleted) {
        try {
          setCompletedSteps(JSON.parse(storedCompleted));
        } catch {
          // corrupt storage — start fresh
        }
      }
      if (storedDismissed === 'true') setIsDismissed(true);
      if (storedStepCount)
        setStepDismissCount(parseInt(storedStepCount, 10) || 0);
      if (storedStepLastAt)
        setStepLastDismissAt(parseInt(storedStepLastAt, 10) || null);
      if (storedAddFileAvailableAfter)
        setAddFileAvailableAfter(
          parseInt(storedAddFileAvailableAfter, 10) || null
        );
      if (storedFirstUpdateAvailableAfter)
        setFirstUpdateAvailableAfter(
          parseInt(storedFirstUpdateAvailableAfter, 10) || null
        );
      if (storedOtherMemberSent === 'true') setOtherMemberSentInNudgeChat(true);
      setLoaded(true);
    };
    loadState();
  }, []);

  // Schedule re-render when retry window opens for current step
  useEffect(() => {
    if (
      stepDismissCount === 0 ||
      stepDismissCount > MAX_STEP_DISMISSES ||
      !stepLastDismissAt
    )
      return;
    const delay = RETRY_DELAYS_MS[stepDismissCount - 1];
    const msUntilShow = stepLastDismissAt + delay - Date.now();
    if (msUntilShow <= 0) {
      setStepNow(Date.now());
      return;
    }
    const t = setTimeout(() => setStepNow(Date.now()), msUntilShow + 100);
    return () => clearTimeout(t);
  }, [stepDismissCount, stepLastDismissAt]);

  // Schedule re-render when the 24h AddFileToChat delay elapses
  useEffect(() => {
    if (!addFileAvailableAfter) return;
    const msUntilShow = addFileAvailableAfter - Date.now();
    if (msUntilShow <= 0) {
      setStepNow(Date.now());
      return;
    }
    const t = setTimeout(() => setStepNow(Date.now()), msUntilShow + 100);
    return () => clearTimeout(t);
  }, [addFileAvailableAfter]);

  // Schedule re-render when the 3h FirstUpdate delay elapses
  useEffect(() => {
    if (!firstUpdateAvailableAfter) return;
    const msUntilShow = firstUpdateAvailableAfter - Date.now();
    if (msUntilShow <= 0) {
      setStepNow(Date.now());
      return;
    }
    const t = setTimeout(() => setStepNow(Date.now()), msUntilShow + 100);
    return () => clearTimeout(t);
  }, [firstUpdateAvailableAfter]);

  const activateNudge = useCallback(
    async (newTaskId: string) => {
      if (!isNative) return;
      // Don't reset if already active for any task (preserves retry state)
      if (
        taskId !== null &&
        !isDismissed &&
        completedSteps.length < MEMBER_TASK_NUDGE_STEPS_ORDERED.length
      )
        return;
      await Promise.all([
        AsyncStorage.setItem(STORAGE_KEY_TASK_ID, newTaskId),
        AsyncStorage.deleteItem(STORAGE_KEY_COMPLETED),
        AsyncStorage.deleteItem(STORAGE_KEY_DISMISSED),
        AsyncStorage.deleteItem(STORAGE_KEY_STEP_DISMISS_COUNT),
        AsyncStorage.deleteItem(STORAGE_KEY_STEP_LAST_DISMISS_AT),
        AsyncStorage.deleteItem(STORAGE_KEY_ADD_FILE_AVAILABLE_AFTER),
        AsyncStorage.deleteItem(STORAGE_KEY_FIRST_UPDATE_AVAILABLE_AFTER),
        AsyncStorage.deleteItem(STORAGE_KEY_OTHER_MEMBER_SENT),
      ]);
      setTaskId(newTaskId);
      setChatIdState(null);
      setCompletedSteps([]);
      setIsDismissed(false);
      setStepDismissCount(0);
      setStepLastDismissAt(null);
      setAddFileAvailableAfter(null);
      setFirstUpdateAvailableAfter(null);
      setOtherMemberSentInNudgeChat(false);
      setStepNow(Date.now());
    },
    [taskId, isDismissed, completedSteps]
  );

  const deactivateNudge = useCallback(async () => {
    if (!isNative) return;
    await Promise.all([
      AsyncStorage.deleteItem(STORAGE_KEY_TASK_ID),
      AsyncStorage.deleteItem(STORAGE_KEY_CHAT_ID),
      AsyncStorage.deleteItem(STORAGE_KEY_COMPLETED),
      AsyncStorage.deleteItem(STORAGE_KEY_DISMISSED),
      AsyncStorage.deleteItem(STORAGE_KEY_STEP_DISMISS_COUNT),
      AsyncStorage.deleteItem(STORAGE_KEY_STEP_LAST_DISMISS_AT),
      AsyncStorage.deleteItem(STORAGE_KEY_ADD_FILE_AVAILABLE_AFTER),
      AsyncStorage.deleteItem(STORAGE_KEY_FIRST_UPDATE_AVAILABLE_AFTER),
      AsyncStorage.deleteItem(STORAGE_KEY_OTHER_MEMBER_SENT),
    ]);
    setTaskId(null);
    setChatIdState(null);
    setCompletedSteps([]);
    setIsDismissed(false);
    setStepDismissCount(0);
    setStepLastDismissAt(null);
    setAddFileAvailableAfter(null);
    setFirstUpdateAvailableAfter(null);
    setOtherMemberSentInNudgeChat(false);
    setStepNow(Date.now());
  }, []);

  const setChatId = useCallback(async (newChatId: string) => {
    if (!isNative) return;
    setChatIdState(newChatId);
    await AsyncStorage.setItem(STORAGE_KEY_CHAT_ID, newChatId);
  }, []);

  const markOtherMemberSentInNudgeChat = useCallback(async () => {
    if (!isNative || otherMemberSentInNudgeChat) return;
    setOtherMemberSentInNudgeChat(true);
    await AsyncStorage.setItem(STORAGE_KEY_OTHER_MEMBER_SENT, 'true');
  }, [otherMemberSentInNudgeChat]);

  const completeStep = useCallback(
    async (step: MemberTaskNudgeStep) => {
      if (!isNative) return;
      if (!taskId || isDismissed) return;
      if (completedSteps.includes(step)) return;

      const stepIndex = MEMBER_TASK_NUDGE_STEPS_ORDERED.indexOf(step);
      // enforce sequential order
      if (
        stepIndex > 0 &&
        !completedSteps.includes(MEMBER_TASK_NUDGE_STEPS_ORDERED[stepIndex - 1])
      ) {
        return;
      }

      const next = [...completedSteps, step];
      setCompletedSteps(next);
      // Reset step retry state for the next step
      setStepDismissCount(0);
      setStepLastDismissAt(null);
      setStepNow(Date.now());

      const storageOps: Promise<void>[] = [
        AsyncStorage.setItem(STORAGE_KEY_COMPLETED, JSON.stringify(next)),
        AsyncStorage.deleteItem(STORAGE_KEY_STEP_DISMISS_COUNT),
        AsyncStorage.deleteItem(STORAGE_KEY_STEP_LAST_DISMISS_AT),
      ];

      // 4B appears 3h after user first opens the nudge task (design spec)
      if (step === MemberTaskNudgeStep.ExploreTask) {
        const availableAfter = Date.now() + 3 * 60 * 60 * 1000;
        setFirstUpdateAvailableAfter(availableAfter);
        storageOps.push(
          AsyncStorage.setItem(
            STORAGE_KEY_FIRST_UPDATE_AVAILABLE_AFTER,
            String(availableAfter)
          )
        );
      }

      // 5B appears 24h after user sends their first group chat message (design spec)
      if (step === MemberTaskNudgeStep.SendFirstChat) {
        const availableAfter = Date.now() + 24 * 60 * 60 * 1000;
        setAddFileAvailableAfter(availableAfter);
        storageOps.push(
          AsyncStorage.setItem(
            STORAGE_KEY_ADD_FILE_AVAILABLE_AFTER,
            String(availableAfter)
          )
        );
      }

      await Promise.all(storageOps);

      trackEvent(MIXPANEL_EVENTS.MEMBER_TASK_NUDGE_STEP_COMPLETED, {
        step,
        completed_count: next.length,
        task_id: taskId,
      });

      if (next.length === MEMBER_TASK_NUDGE_STEPS_ORDERED.length) {
        trackEvent(MIXPANEL_EVENTS.MEMBER_TASK_NUDGE_COMPLETED, {
          task_id: taskId,
        });
      }
    },
    [taskId, isDismissed, completedSteps, trackEvent]
  );

  const dismissCurrentStep = useCallback(async () => {
    if (!isNative) return;

    const currentStepValue =
      MEMBER_TASK_NUDGE_STEPS_ORDERED.find(
        (s) => !completedSteps.includes(s)
      ) ?? null;

    trackEvent(MIXPANEL_EVENTS.MEMBER_TASK_NUDGE_DISMISSED, {
      step: currentStepValue,
      dismiss_count: stepDismissCount + 1,
      task_id: taskId,
    });

    // 4C is "no retry — contextual only": dismissing skips the step permanently
    if (currentStepValue === MemberTaskNudgeStep.SendMessage) {
      await completeStep(MemberTaskNudgeStep.SendMessage);
      return;
    }

    const next = stepDismissCount + 1;
    const ts = Date.now();
    setStepDismissCount(next);
    setStepLastDismissAt(ts);
    setStepNow(ts);
    await Promise.all([
      AsyncStorage.setItem(STORAGE_KEY_STEP_DISMISS_COUNT, String(next)),
      AsyncStorage.setItem(STORAGE_KEY_STEP_LAST_DISMISS_AT, String(ts)),
    ]);

    // Permanently dismiss whole flow if max dismisses reached
    if (next > MAX_STEP_DISMISSES) {
      setIsDismissed(true);
      await AsyncStorage.setItem(STORAGE_KEY_DISMISSED, 'true');
    }
  }, [stepDismissCount, completedSteps, taskId, trackEvent, completeStep]);

  const currentStep = useMemo(() => {
    if (!loaded || !taskId || isDismissed) return null;
    return (
      MEMBER_TASK_NUDGE_STEPS_ORDERED.find(
        (s) => !completedSteps.includes(s)
      ) ?? null
    );
  }, [loaded, taskId, isDismissed, completedSteps]);

  const isCurrentStepVisible = useMemo(() => {
    if (!onboardingFinished) return false;
    if (!currentStep) return false;
    // 4B: only show 3h after ExploreTask was completed
    if (
      currentStep === MemberTaskNudgeStep.FirstUpdate &&
      firstUpdateAvailableAfter &&
      stepNow < firstUpdateAvailableAfter
    )
      return false;
    // 5B: only show 24h after SendFirstChat was completed
    if (
      currentStep === MemberTaskNudgeStep.AddFileToChat &&
      addFileAvailableAfter &&
      stepNow < addFileAvailableAfter
    )
      return false;
    if (stepDismissCount === 0) return true;
    if (stepDismissCount > MAX_STEP_DISMISSES) return false;
    if (!stepLastDismissAt) return true;
    const delay = RETRY_DELAYS_MS[stepDismissCount - 1];
    return stepNow - stepLastDismissAt >= delay;
  }, [
    onboardingFinished,
    currentStep,
    firstUpdateAvailableAfter,
    addFileAvailableAfter,
    stepDismissCount,
    stepLastDismissAt,
    stepNow,
  ]);

  const isComplete =
    completedSteps.length === MEMBER_TASK_NUDGE_STEPS_ORDERED.length;

  const value = useMemo<MemberTaskNudgeContextType>(
    () => ({
      taskId,
      chatId,
      completedSteps,
      isDismissed,
      currentStep,
      isComplete,
      isCurrentStepVisible,
      otherMemberSentInNudgeChat,
      activateNudge,
      deactivateNudge,
      setChatId,
      markOtherMemberSentInNudgeChat,
      completeStep,
      dismissCurrentStep,
    }),
    [
      taskId,
      chatId,
      completedSteps,
      isDismissed,
      currentStep,
      isComplete,
      isCurrentStepVisible,
      otherMemberSentInNudgeChat,
      activateNudge,
      deactivateNudge,
      setChatId,
      markOtherMemberSentInNudgeChat,
      completeStep,
      dismissCurrentStep,
    ]
  );

  return (
    <MemberTaskNudgeContext.Provider value={value}>
      {children}
    </MemberTaskNudgeContext.Provider>
  );
};

export function useMemberTaskNudge(): MemberTaskNudgeContextType {
  const ctx = useContext(MemberTaskNudgeContext);
  if (!ctx) {
    throw new Error(
      'useMemberTaskNudge must be used within MemberTaskNudgeProvider'
    );
  }
  return ctx;
}
